import React, { PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Nullable } from 'global';
import { logError } from 'utils/logger';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useLazyQuery } from '@apollo/client';
import { useFormContext } from 'react-hook-form';
import { useNotifications } from 'modules/notification';
import { useDispatch, useSelector } from 'react-redux';
import { ProposalSaleType } from '@trnsact/trnsact-shared-types';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { useSaleMode } from '../../../context';
import { GET_PROPOSAL_CONFIGURATION } from '../../../api';
import { deskingActions, deskingSelectors } from '../../../model';
import { getProductsRemovedFromMenu } from '../../../lib';
import { Loading } from '../../components/Loading';
import { ProposalConfigResponse, ProposalConfigVariables } from '../../../api/types';
import {
  AftermarketMenu,
  BuilderViewType,
  FinanceProgramFormValues,
  OptionName,
  ProductConfig,
  ProductId,
  SaleStructureType,
  SaleType,
  Term,
} from '../../../types';
import { ProposalAdapterProps } from './types';

export const ProposalAdapter = ({
  children,
  proposalId = null,
  proposalSaleType = null,
  formDefaultValues,
}: PropsWithChildren<ProposalAdapterProps>) => {
  const { reset } = useFormContext<FinanceProgramFormValues>();

  const [loading, setLoading] = useState<boolean>(!!proposalId);
  const prevProposalId = useRef<Nullable<string>>(proposalId ?? null);

  useLayoutEffect(() => {
    if (prevProposalId.current !== proposalId) {
      setLoading(true);
      prevProposalId.current = proposalId;
    }

    return () => {
      prevProposalId.current = null;
    };
  }, [proposalId]);

  const { showNotification } = useNotifications();
  const { onChangeSaleType, onChangeStructureType } = useSaleMode();

  const dispatch = useDispatch();
  const proposalsProductsIds = useSelector<any, string[]>(deskingSelectors.proposalsProductsIds);
  const products = useSelector(deskingSelectors.proposalsProducts);
  const isProductsLoading = useSelector<any, boolean>(deskingSelectors.isProposalsProductsLoading);
  const { handleOpen: openRemovedProductsDialog } = useModal(ModalsKeys.DeskingNotifyAboutRemovedProductsDialog);

  const proposalReceived = ({ proposal }: ProposalConfigResponse) => {
    if (!proposal) return;

    if (!proposal?.settings?.configuration) {
      showNotification('Editing this proposal is not supported.', { type: 'error' });
      setLoading(false);
      return;
    }

    try {
      const { builder, productsConfig, formData, commonProductsConfiguration } = proposal.settings.configuration;

      const formDataToSet = {
        ...(formDefaultValues || {}),
        ...formData,
      } as FinanceProgramFormValues;

      reset(formDataToSet);

      onChangeSaleType(SaleType.Financing);
      onChangeStructureType(SaleStructureType.Finance);
      dispatch(deskingActions.updateMenuBuilderViewType({ type: BuilderViewType.Finance }));

      if (formDataToSet.saleDetails.saleType === SaleType.Cash) {
        onChangeSaleType(SaleType.Cash);
        onChangeStructureType(SaleStructureType.AftermarketMenu);
      }

      if (formDataToSet.financeProgramDescription.editorValue) {
        dispatch(deskingActions.setFPDGenValue({ status: false }));
      }

      if (builder && productsConfig) {
        onChangeStructureType(SaleStructureType.AftermarketMenu);
        const savedBuilder = JSON.parse(builder) as Record<Term, AftermarketMenu>;

        dispatch(
          deskingActions.restoreBuilder({
            builder: savedBuilder,
            commonProductsConfiguration: JSON.parse(commonProductsConfiguration) as Partial<ProductConfig>,
            productsConfig: JSON.parse(productsConfig) as Record<OptionName, Record<ProductId, ProductConfig>>,
          })
        );

        // Products that are no longer available (expired, inactive) are dropped when a saved proposal is reopened
        if (!isProductsLoading && products.length) {
          const removedProducts = getProductsRemovedFromMenu({
            products,
            proposalsProductsIds,
            productsOnMenu: Object.values(savedBuilder).flatMap(menu =>
              menu.menuOptions.flatMap(o => o?.products ?? [])
            ),
          });

          if (removedProducts.length) {
            dispatch(deskingActions.removeProductsFromMenu({ ids: removedProducts.map(p => p.proposalProductId) }));
            openRemovedProductsDialog({ removedProducts });
          }
        }
      }

      if (proposal.submissionIdCreatedFrom) {
        dispatch(deskingActions.setParentProposalId({ parentProposalId: proposalId }));
      }

      dispatch(deskingActions.setIsProposalDataLoaded({ isLoaded: true }));
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
    }
  };

  const [loadProposal, { called: isProposalFetched }] = useLazyQuery<ProposalConfigResponse, ProposalConfigVariables>(
    GET_PROPOSAL_CONFIGURATION,
    {
      variables: {
        id: proposalId!,
      },
      onCompleted: proposalReceived,
      notifyOnNetworkStatusChange: true,
      onError() {
        showNotification('Error', { type: 'error' });
      },
    }
  );

  useEffect(() => {
    if (!isProposalFetched && proposalId) {
      const isProductsExist = !!proposalsProductsIds.length;

      const isReadyForFetch = proposalSaleType === ProposalSaleType.FinanceOnly || isProductsExist;

      if (isReadyForFetch) loadProposal().catch(logError);
    }
  }, [isProposalFetched, proposalId, loadProposal, proposalsProductsIds]);

  return <RenderWithFallback children={children} fallBack={<Loading />} isLoading={loading} />;
};
